/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import React, { useState } from "react";

const DEFAULT_MAX_LENGTH = 40;

interface TruncatedDescriptionProps {
  text: string;
  maxLength?: number;
  style?: React.CSSProperties;
}

/**
 * Renders a description that is truncated to `maxLength` characters.
 * If the text exceeds the limit, shows "...more" in blue.
 * Clicking "more" expands to show the full text (wrapped).
 */
export const TruncatedDescription: React.FC<TruncatedDescriptionProps> = ({
  text,
  maxLength = DEFAULT_MAX_LENGTH,
  style,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  const needsTruncation = text.length > maxLength;

  const baseStyle: React.CSSProperties = {
    display: "block",
    fontSize: "11px",
    fontWeight: "normal",
    color: "rgba(220, 220, 220, 0.7)",
    lineHeight: 1.4,
    wordBreak: "break-word" as const,
    whiteSpace: "normal" as const,
    marginTop: "2px",
    marginBottom: "6px",
    ...style,
  };

  if (!needsTruncation) {
    return <span style={baseStyle}>{text}</span>;
  }

  if (expanded) {
    return (
      <span style={baseStyle}>
        {text}{" "}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              setExpanded(false);
            }
          }}
          style={{
            color: "#60a5fa",
            cursor: "pointer",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          less
        </span>
      </span>
    );
  }

  return (
    <span style={baseStyle}>
      {text.slice(0, maxLength).trimEnd()}...
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
            setExpanded(true);
          }
        }}
        style={{
          color: "#60a5fa",
          cursor: "pointer",
          fontWeight: 600,
          marginLeft: "2px",
          whiteSpace: "nowrap",
        }}
      >
        more
      </span>
    </span>
  );
};

export default TruncatedDescription;
